/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { useServiceContext } from '../../root'
import { useRcmTableData } from '../../hooks/useRcmTableData'

const DEFAULT_PER_PAGE = 10
const TABLE_SORT_DIR = {
  asc: 'asc',
  desc: 'desc'
} as const

interface RcmPaginateProps {
  perPageSizes?: number[]
}

export const RcmPaginate: React.FC<RcmPaginateProps> = ({ perPageSizes = [10, 15, 25, 50, 100, 200] }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const tableData = useRcmTableData()

  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE)
  const [inputPage, setInputPage] = useState('')
  const [isEditingPage, setIsEditingPage] = useState(false)

  useEffect(() => {
    // Load persisted pagination settings
    if (tableData.persistenceKey) {
      const persistedPaginateSettings = context.service.persist.getGlobal<{
        page: number
        perPage: number
      }>('paginate')
      const pageSize =
        persistedPaginateSettings && persistedPaginateSettings.perPage ? persistedPaginateSettings.perPage : null

      if (pageSize) {
        setPerPage(pageSize)
      }
    }
  }, [])

  const handlePaginate = (newPage: number, newPerPage: number) => {
    setPerPage(newPerPage)

    if (tableData.persistenceKey) {
      context.service.persist.setGlobal('paginate', {
        page: newPage,
        perPage: newPerPage
      })
    }

    const persistedSortSettings =
      tableData.persistenceKey && typeof context.service.persist.getBasic === 'function'
        ? context.service.persist.getBasic<{ sort?: string; sortDir?: string }>(tableData.persistenceKey)
        : null

    // Calculate pageStart (0-indexed) from page (1-indexed)
    // Read persisted sort first to avoid stale in-memory sort state on the first page click after sorting.
    const effectiveSortBy = persistedSortSettings?.sort || tableData.sortBy || tableData.defaultSortBy || 'id'
    const effectiveSortDesc =
      persistedSortSettings?.sortDir === TABLE_SORT_DIR.asc || persistedSortSettings?.sortDir === TABLE_SORT_DIR.desc
        ? persistedSortSettings.sortDir === TABLE_SORT_DIR.desc
        : typeof tableData.sortDesc === 'boolean'
          ? tableData.sortDesc
          : !!tableData.defaultSortDesc

    tableData.load({
      sortDesc: effectiveSortDesc,
      sortBy: effectiveSortBy,
      pageSize: newPerPage,
      pageStart: newPage - 1
    })
  }

  const totalPages = Math.ceil(tableData.total / perPage)
  const currentPage = tableData.page

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    // Allow empty string or valid numbers only
    if (value === '' || /^\d+$/.test(value)) {
      const num = parseInt(value, 10)
      // Only set if empty or within valid range
      if (value === '' || (num >= 1 && num <= totalPages)) {
        setInputPage(value)
      }
    }
  }

  const handlePageInputSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const pageNum = parseInt(inputPage, 10)
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      handlePaginate(pageNum, perPage)
    }
    setInputPage('')
  }

  const handlePageInputBlur = () => {
    const pageNum = parseInt(inputPage, 10)
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      handlePaginate(pageNum, perPage)
    }
    setIsEditingPage(false)
    setInputPage('')
  }

  // Calculate page range to display
  const getPageRange = () => {
    const maxPagesToShow = 5
    const halfRange = Math.floor(maxPagesToShow / 2)

    let startPage = Math.max(1, currentPage - halfRange)
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1)

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1)
    }

    const pages = []
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }
    return pages
  }

  const pageRange = getPageRange()

  return (
    <div className="paginate clearfix">
      {tableData.total === 0 ? (
        <div className="total-results">0 {translate('PAGINATION_TOTAL_RESULTS')}</div>
      ) : (
        <>
          <div className="total-results">
            <span>
              {(currentPage - 1) * perPage + 1}-{Math.min(currentPage * perPage, tableData.total)}{' '}
              {translate('PAGINATION_OF_PREPOSITION')} <b>{tableData.total}</b> {translate('PAGINATION_TOTAL_RESULTS')}
            </span>
          </div>
          <div className="page-size-configurator">
            <div className="per-page">
              <span>{translate('PAGINATION_PER_PAGE')}</span>
              <select
                name="itemsPerPage"
                className="form-control"
                value={perPage}
                onChange={(event) => {
                  handlePaginate(1, Number(event.target.value))
                }}
              >
                {perPageSizes.map((pageLength) => (
                  <option key={pageLength} value={pageLength}>
                    {pageLength}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <ul className="controls">
            <li>
              <a
                className={'first' + (currentPage === 1 ? ' disabled' : '')}
                style={{ padding: 0 }}
                onClick={(e) => {
                  e.preventDefault()
                  if (currentPage > 1) {
                    handlePaginate(1, perPage)
                  }
                }}
              >
                <i className="fa fa-angle-double-left" />
              </a>
            </li>
            <li>
              <a
                className={'previous' + (currentPage === 1 ? ' disabled' : '')}
                style={{ padding: 0 }}
                onClick={(e) => {
                  e.preventDefault()
                  if (currentPage > 1) {
                    handlePaginate(currentPage - 1, perPage)
                  }
                }}
              >
                <i className="fa fa-angle-left" />
              </a>
            </li>
            <li className="counter">
              <form onSubmit={handlePageInputSubmit} style={{ display: 'inline' }}>
                <input
                  type="text"
                  value={isEditingPage ? inputPage : currentPage}
                  onChange={handlePageInputChange}
                  onBlur={handlePageInputBlur}
                  onFocus={(e) => {
                    e.target.select()
                    setIsEditingPage(true)
                    setInputPage(currentPage.toString())
                  }}
                  style={{
                    width: '45px',
                    textAlign: 'center',
                    height: '22px',
                    border: '1px solid #c9c9c9',
                    borderRadius: '2px',
                    marginRight: '5px'
                  }}
                />
              </form>
              <span style={{ fontSize: '12px' }}>of {totalPages}</span>
            </li>
            <li>
              <a
                className={'next' + (currentPage === totalPages ? ' disabled' : '')}
                style={{ padding: 0 }}
                onClick={(e) => {
                  e.preventDefault()
                  if (currentPage < totalPages) {
                    handlePaginate(currentPage + 1, perPage)
                  }
                }}
              >
                <i className="fa fa-angle-right" />
              </a>
            </li>
            <li>
              <a
                className={'last' + (currentPage === totalPages ? ' disabled' : '')}
                style={{ padding: 0 }}
                onClick={(e) => {
                  e.preventDefault()
                  if (currentPage < totalPages) {
                    handlePaginate(totalPages, perPage)
                  }
                }}
              >
                <i className="fa fa-angle-double-right" />
              </a>
            </li>
          </ul>
        </>
      )}
    </div>
  )
}
